import ExcelJS from 'exceljs';

export interface ExportLedgerData {
  personName: string;
  balance: number;
  totalReceived: number;
  totalPaid: number;
  currencySymbol: string;
  locale?: string;
  transactions: Array<{
    date: string;
    type: 'credit' | 'debit';
    amount: number;
    note?: string;
    createdAt: string;
  }>;
}

export const generateLedgerExcelBlob = async (data: ExportLedgerData): Promise<Blob> => {
  const {
    personName,
    balance,
    totalReceived,
    totalPaid,
    currencySymbol,
    locale = 'en-IN',
    transactions,
  } = data;

  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'LNB';
  workbook.created = new Date();

  const worksheet = workbook.addWorksheet('Ledger Transactions');

  worksheet.mergeCells('A1:E1');
  const titleCell = worksheet.getCell('A1');
  titleCell.value = `Ledger: ${personName}`;
  titleCell.font = { size: 16, bold: true };
  titleCell.alignment = { horizontal: 'center' };

  worksheet.mergeCells('A2:E2');
  const summaryCell = worksheet.getCell('A2');
  summaryCell.value = `Balance: ${currencySymbol}${parseFloat(balance.toString()).toLocaleString(locale)} | Total Received: ${currencySymbol}${parseFloat(totalReceived.toString()).toLocaleString(locale)} | Total Paid: ${currencySymbol}${parseFloat(totalPaid.toString()).toLocaleString(locale)}`;
  summaryCell.font = { size: 12 };
  summaryCell.alignment = { horizontal: 'center' };

  worksheet.addRow([]);

  worksheet.columns = [
    { header: 'Date', key: 'date', width: 15 },
    { header: 'Type', key: 'type', width: 12 },
    { header: `Amount (${currencySymbol})`, key: 'amount', width: 15 },
    { header: 'Note', key: 'note', width: 40 },
    { header: 'Created At', key: 'createdAt', width: 20 },
  ];

  const headerRow = worksheet.getRow(4);
  headerRow.font = { bold: true, size: 12 };
  headerRow.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFE0E0E0' },
  };
  headerRow.alignment = { horizontal: 'center', vertical: 'middle' };

  transactions.forEach((transaction) => {
    const row = worksheet.addRow({
      date: new Date(transaction.date).toLocaleDateString(locale),
      type: transaction.type === 'credit' ? 'Received' : 'Paid',
      amount: `${currencySymbol}${parseFloat(transaction.amount.toString()).toLocaleString(locale)}`,
      note: transaction.note || '-',
      createdAt: new Date(transaction.createdAt).toLocaleString(locale),
    });

    const typeCell = row.getCell(2);
    if (transaction.type === 'credit') {
      typeCell.font = { color: { argb: 'FF0A8A4A' }, bold: true };
    } else {
      typeCell.font = { color: { argb: 'FFD32F2F' }, bold: true };
    }

    const amountCell = row.getCell(3);
    if (transaction.type === 'credit') {
      amountCell.font = { color: { argb: 'FF0A8A4A' } };
    } else {
      amountCell.font = { color: { argb: 'FFD32F2F' } };
    }
  });

  worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
    if (rowNumber >= 4) {
      row.eachCell((cell) => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        };
      });
    }
  });

  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
};
